import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy, ExternalLink, Key, Plus, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Alert';
import { apiKeysApi } from '../../api/endpoints/apiKeys';
import { formatDate } from '../../lib/formatters';

const CREATE_URL = 'https://00pays.com/api/v1/paylinks/quick';
// Status URL keeps `:paylinkId` as a placeholder so merchants see exactly
// where to drop the id returned by /quick.
const STATUS_URL = 'https://00pays.com/api/v1/paylinks/<paylinkId>/status';

type Language = 'curl' | 'fetch' | 'python';
type ApiAction = 'create' | 'status';

// Lazy-load highlight.js + the github-dark theme from CDN once. We don't add
// hljs to the dashboard's bundle - this modal is the only place that needs it.
let hljsPromise: Promise<any> | null = null;
function loadHljs(): Promise<any> {
  if (hljsPromise) return hljsPromise;
  hljsPromise = new Promise((resolve) => {
    if ((window as any).hljs) return resolve((window as any).hljs);
    if (!document.querySelector('link[data-hljs-theme]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/@highlightjs/cdn-assets@11.10.0/styles/github-dark.min.css';
      link.setAttribute('data-hljs-theme', '1');
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@highlightjs/cdn-assets@11.10.0/highlight.min.js';
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve((window as any).hljs);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return hljsPromise;
}

// Render a code block with syntax highlighting. Falls back to plain text if
// hljs hasn't loaded yet.
function HighlightedBlock({ code, language }: { code: string; language: string }) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    let cancelled = false;
    loadHljs().then((hljs) => {
      if (cancelled || !hljs || !ref.current) return;
      ref.current.removeAttribute('data-highlighted');
      try { hljs.highlightElement(ref.current); } catch { /* ignore */ }
    });
    return () => { cancelled = true; };
  }, [code, language]);
  return (
    <pre className="p-3 pe-12 text-xs leading-relaxed overflow-x-auto whitespace-pre text-start" dir="ltr">
      <code ref={ref} className={`language-${language} font-mono`}>{code}</code>
    </pre>
  );
}

interface ApiExamplePayload {
  title?: string;
  amount?: number;
  customer?: { name?: string; email?: string; phone?: string };
}

interface ApiExampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  payload: ApiExamplePayload;
}

/** Indent + line-break a JSON value to fit nicely in code samples. */
function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function buildBody(p: ApiExamplePayload): Record<string, unknown> {
  const customer: Record<string, string> = {};
  if (p.customer?.name) customer.name = p.customer.name;
  if (p.customer?.email) customer.email = p.customer.email;
  if (p.customer?.phone) customer.phone = p.customer.phone;
  return {
    title: p.title || 'Order #1234',
    amount: p.amount && p.amount > 0 ? p.amount : 99.0,
    ...(Object.keys(customer).length ? { customer } : {}),
    metadata: {
      orderId: 'abc-123',
      channel: 'whatsapp',
    },
  };
}

function buildCreateSnippet(language: Language, body: Record<string, unknown>): string {
  const json = prettyJson(body);
  if (language === 'curl') {
    return `curl -X POST '${CREATE_URL}' \\
  -H 'X-API-Key: <YOUR_API_KEY>' \\
  -H 'Content-Type: application/json' \\
  -d '${json}'`;
  }
  if (language === 'fetch') {
    return `const res = await fetch('${CREATE_URL}', {
  method: 'POST',
  headers: {
    'X-API-Key': '<YOUR_API_KEY>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(${json.replace(/\n/g, '\n  ')}),
});
const { data } = await res.json();
console.log(data.url);`;
  }
  // python
  return `import requests

res = requests.post(
    '${CREATE_URL}',
    headers={
        'X-API-Key': '<YOUR_API_KEY>',
        'Content-Type': 'application/json',
    },
    json=${json.replace(/\n/g, '\n    ')},
)
print(res.json()['data']['url'])`;
}

function buildStatusSnippet(language: Language): string {
  if (language === 'curl') {
    return `curl '${STATUS_URL}' \\
  -H 'X-API-Key: <YOUR_API_KEY>'`;
  }
  if (language === 'fetch') {
    return `const res = await fetch('${STATUS_URL}', {
  headers: { 'X-API-Key': '<YOUR_API_KEY>' },
});
const { data } = await res.json();
if (data.paymentStatus === 'paid') {
  console.log('paid at', data.paidAt, 'order', data.orderId);
}`;
  }
  return `import requests

res = requests.get(
    '${STATUS_URL}',
    headers={'X-API-Key': '<YOUR_API_KEY>'},
)
data = res.json()['data']
if data['paymentStatus'] == 'paid':
    print('paid at', data['paidAt'], 'order', data['orderId'])`;
}

const CREATE_RESPONSE = {
  status: 'ok',
  data: {
    paylinkId: 'a1b2c3d4e5f6g7h8',
    link: 'a1b2c3d4e5f6g7h8',
    url: 'https://00pays.com/paylink/a1b2c3d4e5f6g7h8',
  },
};

const STATUS_RESPONSE_PAID = {
  status: 'ok',
  data: {
    paylinkId: 'a1b2c3d4e5f6g7h8',
    paymentStatus: 'paid',
    mode: 'one-time',
    title: 'Order #1234',
    amount: 99,
    currency: 'SAR',
    paidAt: '2026-05-08T10:30:00.000Z',
    orderId: '653f1a2b3c4d5e6f7a8b9c0d',
    url: 'https://00pays.com/paylink/a1b2c3d4e5f6g7h8',
  },
};

// Map our Language enum to the highlight.js language id.
const HLJS_LANG: Record<Language, string> = {
  curl: 'bash',
  fetch: 'javascript',
  python: 'python',
};

export function ApiExampleModal({ isOpen, onClose, storeId, payload }: ApiExampleModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState<Language>('curl');
  const [action, setAction] = useState<ApiAction>('create');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  // Full plaintext key from the create response. The list endpoint only
  // returns prefixes for security, so this is the merchant's one chance to
  // copy it before we drop it from React state.
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const { data: apiKeysData } = useQuery({
    queryKey: ['api-keys', storeId],
    queryFn: () => apiKeysApi.list(storeId),
    enabled: isOpen && !!storeId,
  });
  const apiKeys = apiKeysData?.apiKeys ?? [];
  const activeKeys = apiKeys.filter((k) => k.isActive);

  const createKeyMutation = useMutation({
    mutationFn: (name: string) =>
      apiKeysApi.create(storeId, {
        name: name.trim() || 'Quick paylinks',
        // Scope the new key to just paylink creation — least privilege for
        // a key the merchant is generating from this lightweight modal.
        permissions: ['paylinks.add'],
      }),
    onSuccess: (result) => {
      setRevealedKey(result.apiKey);
      setShowCreateForm(false);
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: ['api-keys', storeId] });
    },
    onError: () => toast('error', t('common.error')),
  });

  const body = useMemo(() => buildBody(payload), [payload]);
  const snippet = useMemo(
    () => action === 'create' ? buildCreateSnippet(language, body) : buildStatusSnippet(language),
    [language, body, action],
  );
  const exampleResponse = action === 'create' ? CREATE_RESPONSE : STATUS_RESPONSE_PAID;
  const endpointUrl = action === 'create' ? CREATE_URL : STATUS_URL;
  const endpointMethod = action === 'create' ? 'POST' : 'GET';

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast('success', t('settings.copiedToClipboard', 'Copied to clipboard'));
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast('error', t('settings.copyFailed', 'Failed to copy'));
    }
  };

  const goToApiKeysSettings = () => {
    onClose();
    navigate(`/${storeId}/settings?tab=apiKeys`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('paylinks.apiQuickReference', 'API Quick Reference')}
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close', 'Close')}
        </Button>
      }
    >
      <div className="space-y-5">
        {/* Action toggle: Create vs Status */}
        <section>
          <div
            role="tablist"
            className="inline-flex rounded-lg border border-border-default bg-bg-tertiary p-1 text-xs"
          >
            {(['create', 'status'] as ApiAction[]).map((a) => {
              const isActive = action === a;
              return (
                <button
                  key={a}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setAction(a)}
                  className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                    isActive
                      ? 'bg-bg-primary text-text-primary shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {a === 'create'
                    ? t('paylinks.actionCreate', 'Create paylink')
                    : t('paylinks.actionStatus', 'Check payment status')}
                </button>
              );
            })}
          </div>
        </section>

        {/* Endpoint */}
        <section>
          <p className="text-xs uppercase tracking-wide text-text-muted mb-1.5">
            {t('paylinks.endpoint', 'Endpoint')}
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-border-default bg-bg-tertiary px-3 py-2" dir="ltr">
            <span
              className={`rounded px-2 py-0.5 text-[11px] font-bold ${
                endpointMethod === 'POST'
                  ? 'bg-success/15 text-success'
                  : 'bg-accent/15 text-accent'
              }`}
            >
              {endpointMethod}
            </span>
            <code className="flex-1 truncate text-sm font-mono">{endpointUrl}</code>
            <button
              type="button"
              onClick={() => handleCopy(endpointUrl, 'endpoint')}
              className="rounded p-1 text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
              title={t('common.copy', 'Copy')}
            >
              {copiedField === 'endpoint' ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          {action === 'status' && (
            <p className="mt-1.5 text-[11px] text-text-muted">
              {t(
                'paylinks.statusUrlHint',
                'Replace <paylinkId> with the value returned by the create endpoint.',
              )}
            </p>
          )}
        </section>

        {/* API keys */}
        <section>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs uppercase tracking-wide text-text-muted">
              {t('settings.apiKeys', 'API Keys')}
            </p>
            <button
              type="button"
              onClick={goToApiKeysSettings}
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
            >
              {t('paylinks.manageKeys', 'Manage keys')} <ExternalLink className="h-3 w-3" />
            </button>
          </div>

          {/* Just-created plaintext key — only chance to copy. */}
          {revealedKey && (
            <div className="mb-2 rounded-lg border border-success/30 bg-success/5 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-success">
                    {t('paylinks.keyCreated', 'API key created')}
                  </p>
                  <p className="mt-0.5 text-[11px] text-text-muted">
                    {t('paylinks.keyCreatedHint', 'Copy it now — for security, you won\'t be able to see it again.')}
                  </p>
                  <div className="mt-2 flex items-center gap-2 rounded-md border border-border-default bg-bg-primary px-2 py-1.5" dir="ltr">
                    <code className="flex-1 truncate text-xs font-mono text-text-primary">{revealedKey}</code>
                    <button
                      type="button"
                      onClick={() => handleCopy(revealedKey, 'revealed')}
                      className="rounded p-1 text-text-muted hover:text-accent hover:bg-bg-hover transition-colors"
                      title={t('common.copy', 'Copy')}
                    >
                      {copiedField === 'revealed' ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setRevealedKey(null)}
                  className="rounded p-1 text-text-muted hover:text-text-primary"
                  title={t('common.dismiss', 'Dismiss')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {activeKeys.length > 0 && (
            <div className="rounded-lg border border-border-default overflow-hidden mb-2">
              {activeKeys.map((k, i) => (
                <div
                  key={k._id}
                  className={`flex items-center gap-3 px-3 py-2 text-sm ${i > 0 ? 'border-t border-border-default' : ''}`}
                >
                  <Key className="h-3.5 w-3.5 text-text-muted shrink-0" />
                  <span className="font-medium truncate">{k.name}</span>
                  <code className="text-xs text-text-muted truncate" dir="ltr">{k.keyPrefix}…</code>
                  <span className="ms-auto text-[11px] text-text-muted shrink-0">
                    {k.lastUsed ? `${t('settings.lastUsed', 'Last used')}: ${formatDate(k.lastUsed)}` : t('settings.neverUsed', 'Never used')}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Inline create flow */}
          {showCreateForm ? (
            <div className="rounded-lg border border-border-default bg-bg-tertiary p-3">
              <label className="block text-xs text-text-muted mb-1.5">
                {t('settings.keyName', 'Key name')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder={t('paylinks.keyNamePlaceholder', 'e.g. WhatsApp bot')}
                  className="flex-1 rounded-md border border-border-default bg-bg-primary px-3 py-1.5 text-sm outline-none focus:border-accent"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') createKeyMutation.mutate(newKeyName);
                    if (e.key === 'Escape') { setShowCreateForm(false); setNewKeyName(''); }
                  }}
                />
                <Button
                  size="sm"
                  isLoading={createKeyMutation.isPending}
                  onClick={() => createKeyMutation.mutate(newKeyName)}
                >
                  {t('common.create', 'Create')}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setShowCreateForm(false); setNewKeyName(''); }}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-text-muted">
                {t('paylinks.scopedToPaylinksAdd', 'Scoped to paylinks.add only. Add more permissions later in Settings.')}
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border-default bg-bg-tertiary px-3 py-2 text-sm text-text-secondary hover:border-accent/50 hover:text-accent transition-colors w-full justify-center"
            >
              <Plus className="h-3.5 w-3.5" />
              {activeKeys.length === 0
                ? t('paylinks.createFirstKey', 'Create your first API key')
                : t('paylinks.createAnotherKey', 'Create another key')}
            </button>
          )}

          <p className="mt-2 text-[11px] text-text-muted">
            {t('paylinks.keyValueHint', 'Full key values are only shown when first created. Replace <YOUR_API_KEY> in the snippet below.')}
          </p>
        </section>

        {/* Language tabs */}
        <section>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs uppercase tracking-wide text-text-muted">
              {t('paylinks.example', 'Example')}
            </p>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as Language)}
              className="rounded-md border border-border-default bg-bg-tertiary px-2 py-1 text-xs outline-none focus:border-accent"
            >
              <option value="curl">cURL</option>
              <option value="fetch">JavaScript (fetch)</option>
              <option value="python">Python (requests)</option>
            </select>
          </div>
          <div className="relative rounded-lg border border-border-default bg-bg-tertiary overflow-hidden" dir="ltr">
            <HighlightedBlock code={snippet} language={HLJS_LANG[language]} />
            <button
              type="button"
              onClick={() => handleCopy(snippet, 'snippet')}
              className="absolute right-2 top-2 rounded-md border border-border-default bg-bg-secondary p-1.5 text-text-muted hover:text-accent hover:border-accent/50 transition-colors"
              title={t('common.copy', 'Copy')}
            >
              {copiedField === 'snippet' ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
        </section>

        {/* Example response */}
        <section>
          <p className="text-xs uppercase tracking-wide text-text-muted mb-1.5">
            {t('paylinks.exampleResponse', 'Example response')}
          </p>
          <div className="relative rounded-lg border border-border-default bg-bg-tertiary overflow-hidden" dir="ltr">
            <HighlightedBlock code={prettyJson(exampleResponse)} language="json" />
          </div>
          <p className="mt-1.5 text-[11px] text-text-muted">
            {action === 'create'
              ? t(
                  'paylinks.urlAlwaysHostHint',
                  'Quick paylinks are always hosted at 00pays.com so you can share them anywhere.',
                )
              : t(
                  'paylinks.statusPollHint',
                  'Poll this every few seconds from your CRM/POS until paymentStatus becomes "paid".',
                )}
          </p>
        </section>
      </div>
    </Modal>
  );
}
